/**
 * Utility for displaying environment configuration errors.
 * This is used before Angular bootstraps, so it uses vanilla DOM manipulation.
 */

export interface EnvironmentErrorOptions {
  errorMessage: string;
  errorDetails?: string;
  title?: string;
  icon?: string;
  helpText?: string;
  retryButtonText?: string;
}

/**
 * Default styling constants for the error page
 */
const STYLES = {
  body: 'margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;',
  container: `
    display: flex;
    min-height: 100vh;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 20px;
    box-sizing: border-box;
  `,
  card: `
    background: white;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    padding: 40px;
    max-width: 600px;
    width: 100%;
    text-align: center;
  `,
  icon: `
    font-size: 64px;
    margin-bottom: 20px;
  `,
  title: `
    color: #721c24;
    font-size: 28px;
    font-weight: 600;
    margin: 0 0 16px 0;
  `,
  message: `
    color: #333;
    font-size: 16px;
    line-height: 1.6;
    margin: 0 0 24px 0;
  `,
  detailsContainer: `
    background-color: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 8px;
    padding: 16px;
    margin: 0 0 24px 0;
    text-align: left;
  `,
  detailsLabel: `
    color: #856404;
    font-size: 14px;
    display: block;
    margin-bottom: 8px;
  `,
  details: `
    color: #856404;
    font-size: 13px;
    margin: 0;
    font-family: 'Monaco', 'Courier New', monospace;
    word-break: break-word;
  `,
  helpText: `
    color: #666;
    font-size: 14px;
    margin: 0 0 24px 0;
    line-height: 1.5;
  `,
  button: `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 6px;
    padding: 12px 32px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  `
};

/**
 * Creates and appends an error page to the document body.
 * Displays a styled error page with message, details, and retry button.
 */
export function showEnvironmentLoadError(options: EnvironmentErrorOptions): void {
  const {
    errorMessage,
    errorDetails,
    title = 'Configuration Error',
    icon = '⚠️',
    helpText = 'Please contact your system administrator if this problem persists.',
    retryButtonText = 'Retry'
  } = options;

  const setupErrorPage = (): void => {
    // Clear the entire document body
    document.body.innerHTML = '';
    document.body.style.cssText = STYLES.body;

    // Create the error page container
    const errorPage = document.createElement('div');
    errorPage.style.cssText = STYLES.container;

    // Create the error card
    const errorCard = document.createElement('div');
    errorCard.style.cssText = STYLES.card;

    // Error icon
    const iconElement = document.createElement('div');
    iconElement.style.cssText = STYLES.icon;
    iconElement.textContent = icon;
    errorCard.appendChild(iconElement);

    // Error title
    const titleElement = document.createElement('h1');
    titleElement.style.cssText = STYLES.title;
    titleElement.textContent = title;
    errorCard.appendChild(titleElement);

    // Error message
    const messageElement = document.createElement('p');
    messageElement.style.cssText = STYLES.message;
    messageElement.textContent = errorMessage;
    errorCard.appendChild(messageElement);

    // Error details (if provided)
    if (errorDetails) {
      const detailsContainer = document.createElement('div');
      detailsContainer.style.cssText = STYLES.detailsContainer;

      const detailsLabel = document.createElement('strong');
      detailsLabel.style.cssText = STYLES.detailsLabel;
      detailsLabel.textContent = 'Error Details:';
      detailsContainer.appendChild(detailsLabel);

      const detailsElement = document.createElement('p');
      detailsElement.style.cssText = STYLES.details;
      detailsElement.textContent = errorDetails;
      detailsContainer.appendChild(detailsElement);

      errorCard.appendChild(detailsContainer);
    }

    // Help text
    const helpTextElement = document.createElement('p');
    helpTextElement.style.cssText = STYLES.helpText;
    helpTextElement.textContent = helpText;
    errorCard.appendChild(helpTextElement);

    // Refresh button
    const refreshButton = document.createElement('button');
    refreshButton.style.cssText = STYLES.button;
    refreshButton.textContent = retryButtonText;
    
    // Button hover effects
    refreshButton.onmouseover = () => {
      refreshButton.style.transform = 'translateY(-2px)';
      refreshButton.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
    };
    refreshButton.onmouseout = () => {
      refreshButton.style.transform = 'translateY(0)';
      refreshButton.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
    };
    refreshButton.onclick = () => {
      window.location.reload();
    };
    errorCard.appendChild(refreshButton);

    errorPage.appendChild(errorCard);

    // Add the error page to the body
    document.body.appendChild(errorPage);
  };

  // Execute setup when DOM is ready
  if (document.body) {
    setupErrorPage();
  } else {
    document.addEventListener('DOMContentLoaded', setupErrorPage);
    // Also try to set up if document is already loaded
    if (document.readyState !== 'loading') {
      setupErrorPage();
    }
  }
}

